/**
 * RegionCard — one scored region (spz-09-results-views).
 *
 * The headline is the worst-off household: max door-to-door burden over households.
 * There is deliberately no average anywhere on this card — an outlier household must be
 * visible at a glance, not smoothed away.
 */
import type { RegionStatus } from '../lib/types'
import { BurdenStrip, formatHours, type HouseholdBurden } from './BurdenStrip'

/**
 * A scored region: the shape the burden engine ranks and returns.
 *
 * TODO: import from #18 (`src/lib/burden/types.ts`, the `region-score` contract) once it
 * merges, and delete this stub. `RegionStatus` above is already the real enum from
 * `src/lib/types.ts`; `water_type` is widened to `| null` to match `Region.water_type`.
 */
export interface RegionScore {
  region_id: string
  name: string
  water_type: string | null
  status: RegionStatus
  /** max over households — never an average */
  worst_burden_hours: number
  per_household: HouseholdBurden[]
  /** Label of the household that suggested this, or null when the model proposed it. */
  suggested_by?: string | null
}

const REGION_CSS = `
.spz-region-head { display: flex; align-items: flex-start; gap: 0.5rem; justify-content: space-between; }
.spz-region-name { font-size: 1.15rem; font-weight: 700; letter-spacing: -0.01em; }
.spz-region-water { font-size: 0.8rem; color: var(--muted); text-transform: capitalize; }
.spz-region-by { font-size: 0.78rem; color: var(--muted); margin-top: 0.15rem; }
.spz-unscored { font-weight: 600; color: var(--muted); }
.spz-status-proposed { border-color: var(--spz-line-strong); color: var(--ink); }
.spz-status-active { border-color: var(--accent); color: var(--accent); background: var(--spz-accent-soft); }
.spz-status-rejected { border-style: dashed; border-color: var(--spz-line); color: var(--muted); }
.spz-region-card.is-rejected { opacity: 0.68; }
.spz-worst {
  font-size: 0.98rem;
  line-height: 1.35;
  border-left: 3px solid var(--spz-line-strong);
  padding-left: 0.6rem;
}
.spz-worst strong { font-weight: 800; }
.spz-worst .spz-worst-k {
  display: block;
  font-size: 0.72rem;
  text-transform: uppercase;
  letter-spacing: 0.07em;
  color: var(--muted);
  font-weight: 700;
}
.spz-region-err { font-size: 0.85rem; color: var(--spz-warn); }
`

export interface RegionCardProps {
  region: RegionScore
  householdLabel: (id: string) => string
  isOrganizer: boolean
  busy?: boolean
  error?: string | null
  onActivate?: (regionId: string) => void
  onReject?: (regionId: string) => void
}

export function RegionCard({
  region,
  householdLabel,
  isOrganizer,
  busy = false,
  error = null,
  onActivate,
  onReject,
}: RegionCardProps) {
  const worst = region.per_household.find((h) => h.is_worst)
  const showDecide = isOrganizer && region.status === 'proposed'

  return (
    <article className={`spz-card spz-region-card${region.status === 'rejected' ? ' is-rejected' : ''}`}>
      <style href="spz-region-card" precedence="spz">
        {REGION_CSS}
      </style>

      <div className="spz-region-head">
        <div>
          <div className="spz-region-name">{region.name}</div>
          {region.water_type ? (
            <div className="spz-region-water">{region.water_type}</div>
          ) : null}
          {region.suggested_by ? (
            <div className="spz-region-by">Suggested by {region.suggested_by}</div>
          ) : null}
        </div>
        <span className={`spz-pill spz-status-${region.status}`}>{region.status}</span>
      </div>

      <p className="spz-worst">
        <span className="spz-worst-k">Worst-off household</span>
        {worst ? (
          <>
            <strong>{householdLabel(worst.household_id)}</strong> &mdash;{' '}
            <strong>{formatHours(worst.door_to_door_hours)}</strong> door-to-door
          </>
        ) : (
          // Never render worst_burden_hours here: it is 0 for a region nobody has
          // measured, and "0 h" reads as the best possible trip.
          <span className="spz-unscored">Not scored yet &mdash; needs a re-score</span>
        )}
      </p>

      <BurdenStrip households={region.per_household} householdLabel={householdLabel} />

      {error ? <p className="spz-region-err">{error}</p> : null}

      {showDecide ? (
        <div className="spz-btn-row">
          <button
            type="button"
            className="spz-btn"
            disabled={busy}
            onClick={() => onActivate?.(region.region_id)}
          >
            Activate
          </button>
          <button
            type="button"
            className="spz-btn spz-btn-ghost"
            disabled={busy}
            onClick={() => onReject?.(region.region_id)}
          >
            Reject
          </button>
        </div>
      ) : null}
    </article>
  )
}

export default RegionCard
